import React from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";

const AssignmentEditDialog = ({
  open,
  onOpenChange,
  formData,
  setFormData,
  schedules,
  cyclePatterns,
  handleUpdateAssignment,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("planning.assignmentsPanel.editTitle")}</DialogTitle>
          <DialogDescription>{t("planning.assignmentsPanel.editDescription")}</DialogDescription>
        </DialogHeader>

      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label>{t("planning.assignmentsPanel.assignmentType")}</Label>
          <Select
            value={formData.assignment_type}
            onValueChange={(value) => setFormData({ ...formData, assignment_type: value })}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="schedule">{t("planning.assignmentsPanel.fixedSchedule")}</SelectItem>
              <SelectItem value="cycle">{t("planning.assignmentsPanel.rotatingCycle")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {formData.assignment_type === "schedule" ? (
          <div className="space-y-2">
            <Label>{t("planning.assignmentsPanel.schedule")}</Label>
            <Select
              value={formData.schedule_id}
              onValueChange={(value) => setFormData({ ...formData, schedule_id: value })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {schedules.map((s) => (
                  <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <Label>{t("planning.assignmentsPanel.cycle")}</Label>
              <Select
                value={formData.cycle_pattern_id}
                onValueChange={(value) => setFormData({ ...formData, cycle_pattern_id: value })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {cyclePatterns.map((c) => (
                    <SelectItem key={c.id} value={c.id.toString()}>
                      {c.name} ({t("planning.cycles.weekCount", { count: c.cycle_duration_weeks })})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("planning.assignmentsPanel.cycleStartWeek")}</Label>
              <Input
                type="number"
                min="1"
                value={formData.cycle_start_week}
                onChange={(e) => setFormData({ ...formData, cycle_start_week: parseInt(e.target.value) || 1 })}
              />
            </div>
          </>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t("planning.assignmentsPanel.startDate")}</Label>
            <Input
              type="date"
              value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>{t("planning.assignmentsPanel.endDate")}</Label>
            <Input
              type="date"
              value={formData.end_date}
              onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              min={formData.start_date}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t("planning.assignmentsPanel.editReason")}</Label>
          <Textarea
            placeholder={t("planning.assignmentsPanel.editReasonPlaceholder")}
            value={formData.reason}
            onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
            rows={2}
          />
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>{t("planning.scheduleManager.actions.cancel")}</Button>
        <Button onClick={handleUpdateAssignment}>{t("planning.scheduleManager.actions.save")}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};

export default AssignmentEditDialog;
